import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { useAddConquestModeMutation, useConquestModeQuery, useDisableConquestModeMutation, useRegionsConquestModeQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import Iconify from "src/components/iconify";
import { DataGridPro, GridColumnVisibilityModel, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { formatNumber } from "src/utils/format-number";
import ModalPreview from "./modal/modal-preview";
import ModalCreateConquestMode from "./modal/modal-create-conquest-mode";
import ModalDisabledConquestMode from "./modal/modal-disabled-conquest-mode";
import { enqueueSnackbar } from "notistack";
import "./conquest-mode-grid.scss";
import { CountryBar, CountryItem } from "src/components/country-bar";
import { useCountryData } from "src/components/visibility";
import { PAGE_PADDING_X } from "../../funnel/constants";
import { CountryTabLabel } from "../../explore/CountryTabLabel";
import { HEADER } from "src/layouts/config-layout";
import { usePersistedCountries } from "../../explore/usePersistedCountries";
import { setFilterCountry } from "src/context/reducers/filter-settings/filterSlice";
import PresenceRateChartNew from "src/components/visibility/components/PresenceRateChartNew";
import CustomToolbar from './components/ConquestModeToolbar';
import CustomGridPanel from './components/ConquestModeGridPanel';
import ConquestModeFooter from "./components/ConquestModeFooter";
import { formatCurrency } from "src/utils/format-currency";
import { useLocales } from "src/locales";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import ConquestModeAnalysisChartModal from './modal/conquest-mode-analysis-chart-modal';

const OtherCompetitors = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M10.5001 9.23818C11.3494 9.66481 12.0776 10.3495 12.609 11.2056C12.7143 11.3752 12.7669 11.4599 12.7851 11.5773C12.8221 11.8159 12.659 12.1091 12.4368 12.2035C12.3274 12.25 12.2045 12.25 11.9585 12.25M9.33348 6.72714C10.1978 6.2976 10.7918 5.40567 10.7918 4.375C10.7918 3.34433 10.1978 2.4524 9.33348 2.02286M8.16682 4.375C8.16682 5.82475 6.99156 7 5.54182 7C4.09207 7 2.91682 5.82475 2.91682 4.375C2.91682 2.92525 4.09207 1.75 5.54182 1.75C6.99156 1.75 8.16682 2.92525 8.16682 4.375ZM1.49303 11.0474C2.42305 9.65099 3.89062 8.75 5.54181 8.75C7.19301 8.75 8.66059 9.65099 9.5906 11.0474C9.79434 11.3533 9.89621 11.5062 9.88448 11.7016C9.87535 11.8538 9.77562 12.04 9.65406 12.1319C9.49794 12.25 9.28322 12.25 8.85377 12.25H2.22986C1.80042 12.25 1.58569 12.25 1.42957 12.1319C1.30801 12.04 1.20828 11.8538 1.19915 11.7016C1.18742 11.5062 1.28929 11.3533 1.49303 11.0474Z" stroke="#F7C2C0" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
    );
}

const renderGridHeader = (label: string, group = false) => (
    <Box
        component="span"
        className={group ? 'conquest-mode-grid__group-label' : 'conquest-mode-grid__header-label'}
    >
        {label}
    </Box>
);

const EvolutionValue = ({
    value,
    format = 'percent',
    formattedAbsoluteValue,
}: {
    value: number | null;
    format?: 'percent' | 'pp' | 'absolute';
    formattedAbsoluteValue?: string;
}) => {
    if (value === null || !Number.isFinite(value) || value === 0) {
        return null;
    }
    const positive = value > 0;
    const suffix = format === 'pp' ? 'pp' : '%';
    let formattedValue: string;
    if (format === 'absolute') {
        formattedValue = `${positive ? '+' : '-'}${formattedAbsoluteValue ?? formatNumber(Math.abs(value))}`;
    } else if (value > 999) {
        formattedValue = `>999${suffix}`;
    } else if (value < -999) {
        formattedValue = `<-999${suffix}`;
    } else {
        formattedValue = `${positive ? '+' : ''}${value.toFixed(0)}${suffix}`;
    }

    return (
        <Typography
            component="span"
            sx={{
                marginLeft: 'auto',
                flexShrink: 0,
                color: positive ? '#0DB46B' : '#E03535',
                textAlign: 'right',
                fontVariantNumeric: 'lining-nums tabular-nums',
                fontFamily: '"Public Sans"',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '14.4px',
                whiteSpace: 'nowrap',
            }}
        >
            {formattedValue}
        </Typography>
    );
};

const PaidRateCell = ({
    current,
    compare,
    showEvolution,
    evolutionFormat,
}: {
    current: number | null;
    compare: number | null;
    showEvolution: boolean;
    evolutionFormat: 'percent' | 'pp';
}) => {
    const deltaPp = current === null || compare === null ? null : current - compare;
    const deltaPercent = current === null || compare === null || compare === 0
        ? current === 0 && compare === 0 ? 0 : null
        : ((current - compare) / compare) * 100;

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: showEvolution ? 'space-between' : 'flex-end',
            }}
        >
            <Typography
                sx={{
                    flexShrink: 0,
                    color: '#2F3843',
                    textAlign: 'right',
                    fontFamily: '"Public Sans"',
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '22px',
                    whiteSpace: 'nowrap',
                }}
            >
                {current === null ? "—" : `${Math.round(current)}%`}
            </Typography>
            {showEvolution && (
                <EvolutionValue
                    value={evolutionFormat === 'percent' ? deltaPercent : deltaPp}
                    format={evolutionFormat}
                />
            )}
        </Box>
    );
};

const MetricProgressCell = ({
    value,
    formattedValue,
    maxValue,
    color,
    valueWidth,
    evolution,
    evolutionFormat = 'percent',
    formattedAbsoluteEvolution,
    showEvolution,
}: {
    value: number | null;
    formattedValue: string;
    maxValue: number;
    color: string;
    valueWidth: number;
    evolution?: number | null;
    evolutionFormat?: 'percent' | 'absolute';
    formattedAbsoluteEvolution?: string;
    showEvolution?: boolean;
}) => {
    const safeValue = value ?? 0;
    const progress = maxValue > 0
        ? Math.min((safeValue / maxValue) * 100, 100)
        : 0;
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: showEvolution ? '8px' : 0,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                <Typography
                    sx={{
                        width: `${valueWidth}px`,
                        minWidth: `${valueWidth}px`,
                        flexShrink: 0,
                        color: '#2F3843',
                        textAlign: 'left',
                        fontFamily: '"Public Sans"',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '22px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {formattedValue}
                </Typography>
                {value !== null && <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        height: '6px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: color,
                            borderRadius: '4px',
                        }}
                    />
                </Box>}
            </Box>
            {showEvolution && (
                <EvolutionValue
                    value={evolution ?? null}
                    format={evolutionFormat}
                    formattedAbsoluteValue={formattedAbsoluteEvolution}
                />
            )}
        </Box>
    );
};

const VisibilityRateCell = ({
    total,
    compareTotal,
    data,
    colors,
    showEvolution,
    evolutionFormat,
}: {
    total: number | null;
    compareTotal: number | null;
    data: {
        organicOnly: number | null;
        soloBidder: number | null;
        topAdPosition: number | null;
        competitorOnly: number | null;
    };
    colors: string[];
    showEvolution: boolean;
    evolutionFormat: 'percent' | 'pp';
}) => {
    const deltaPp = total === null || compareTotal === null
        ? null
        : total - compareTotal;

    const deltaPercent = total === null || compareTotal === null || compareTotal === 0
        ? total === 0 && compareTotal === 0
            ? 0
            : null
        : ((total - compareTotal) / compareTotal) * 100;

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: showEvolution ? '8px' : 0,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        width: '32px',
                        minWidth: '32px',
                        flexShrink: 0,
                        color: '#2F3843',
                        textAlign: 'right',
                        fontFamily: '"Public Sans"',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '22px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {total === null ? "—" : `${Math.round(total)}%`}
                </Typography>

                {total !== null && total > 0 && <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <PresenceRateChartNew
                        round={1}
                        data={{ organicOnly: data.organicOnly ?? 0, soloBidder: data.soloBidder ?? 0, topAdPosition: data.topAdPosition ?? 0, competitorOnly: data.competitorOnly ?? 0 }}
                        totalVisibilityRate={total}
                        colors={colors}
                        title="Competitor visibility rate"
                        labels={{
                            organicOnly: 'Organic only',
                            soloBidder: 'Competitor Solo bidder',
                            topAdPosition: 'Competitor Top Ad',
                            competitorsOnly: 'Other Competitors',
                        }}
                        icons={{
                            competitorsOnly: OtherCompetitors(),
                        }}
                        height={12}
                        sx={{
                            width: '100%',
                            minWidth: 0,
                        }}
                        barSx={{
                            width: '100%',
                            height: '12px',
                            gap: '4px',
                            borderRadius: '2px',
                            overflow: 'visible',
                        }}
                        segmentSx={{
                            height: '12px',
                            borderRadius: '2px',
                            minWidth: '4px',
                        }}
                    />
                </Box>}
            </Box>

            {showEvolution && (
                <EvolutionValue
                    value={
                        evolutionFormat === 'percent'
                            ? deltaPercent
                            : deltaPp
                    }
                    format={evolutionFormat}
                />
            )}
        </Box>
    );
};

export default function ConquestMode() {
    const filterStore = useSelector((state: any) => state.filters);
    const workspace: any = useCurrentWorkspace()!;
    const apiRef: any = useRef();
    const toolbarRef: any = useRef();
    const dispatch = useDispatch();

    const [addConquestMode] = useAddConquestModeMutation();

    const [disableConquestMode] = useDisableConquestModeMutation();

    const { currentLang } = useLocales();
    const selectedCountries = filterStore?.countries ?? [];
    const [selectedPeriod, setSelectedPeriod] = useState<'l7d' | 'l14d' | 'l30d' | 'l90d'>('l7d');
    const [selectedComparison, setSelectedComparison] = useState<'none' | 'pp' | 'ly'>('none');
    const [showAnalysisChart, setShowAnalysisChart] = useState(false);

    const { data: conquestModeData, isLoading: isLoadingConquestModeData, isFetching: isFetchingConquestModeData, refetch } = useConquestModeQuery(
        {
            workspace_id: workspace?.id,
            client_id: workspace?.bqTable,
            region_name: selectedCountries,
            period: selectedPeriod,
            comparison: selectedComparison,
        },
        {
            skip: !workspace?.id || selectedCountries.length === 0,
            refetchOnMountOrArgChange: true,
        }
    );

    const { isLoading: isLoadingCountries }: any = useRegionsConquestModeQuery({ workspace_id: workspace?.id as number }, { skip: !workspace?.id });
    const [showEvolution, setShowEvolution] = useState(false);
    const [evolutionFormat, setEvolutionFormat] = useState<'percent' | 'pp'>('pp');

    useEffect(() => {
        setShowEvolution(selectedComparison !== 'none');
    }, [selectedComparison]);

    const getMetricKey = (metric: string) => `${metric}_${selectedPeriod}`;

    const getComparisonMetricKey = (metric: string) => {
        if (selectedComparison === 'none') {
            return null;
        }
        return `${metric}_${selectedPeriod}_${selectedComparison}`;
    };

    const toMetricNumber = (value: unknown): number | null => {
        if (value === null || value === undefined || value === '') return null;
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    };

    // Temporary UI fallback for legacy API rows that encode absence as zero.
    // Prefer an explicit has_data/has_scraping_data flag supplied by the API.
    const metricFields = [
        'paid_top_rate', 'paid_first_rate', 'competitor_visibility_rate',
        'ad_clicks', 'ad_cost', 'avg_cpc', 'ad_conversions', 'ad_revenue',
    ];

    const isMissingDataRow = (row: any): boolean => {
        if (!row || row.isParent === true) return false;
        if (row.has_data === false || row.hasData === false || row.has_scraping_data === false) return true;
        if (row.has_data === true || row.hasData === true || row.has_scraping_data === true) return false;

        // Legacy fallback: all measured fields equal to zero or are absent.
        // A true all-zero observation cannot be distinguished without API metadata.
        const values = metricFields.map((field) => {
            const periodValue = toMetricNumber(row[`${field}_${selectedPeriod}`]);
            return periodValue ?? toMetricNumber(row[field]);
        });
        return values.every((value) => value === null || value === 0);
    };

    const getCurrentValue = (row: any, metric: string, fallbackMetric?: string): number | null => {
        if (isMissingDataRow(row)) return null;
        for (const key of [getMetricKey(metric), metric, ...(fallbackMetric ? [getMetricKey(fallbackMetric), fallbackMetric] : [])]) {
            const value = toMetricNumber(row?.[key]);
            if (value !== null) return value;
        }
        return null;
    };

    const getCompareValue = (row: any, metric: string, fallbackMetric?: string): number | null => {
        if (selectedComparison === 'none') return null;
        const keys = [getComparisonMetricKey(metric), `${metric}_${selectedComparison}`, `${metric}_compare`];
        if (fallbackMetric) keys.push(getComparisonMetricKey(fallbackMetric), `${fallbackMetric}_${selectedComparison}`, `${fallbackMetric}_compare`);
        for (const key of keys) {
            if (!key) continue;
            const value = toMetricNumber(row?.[key]);
            if (value !== null) return value;
        }
        return null;
    };

    const getPercentageEvolution = (current: number | null, compare: number | null) => {
        if (current === null || compare === null) return null;
        if (compare === 0) return current === 0 ? 0 : null;
        return ((current - compare) / compare) * 100;
    };

    const getAdsEvolution = (current: number | null, compare: number | null) =>
        evolutionFormat === 'percent'
            ? getPercentageEvolution(current, compare)
            : current === null || compare === null ? null : current - compare;

    const { countriesData: countries, isLoading: isLoadingCountryData } = useCountryData();
    const [countryList, setCountryList] = useState<CountryItem[]>([]);

    useEffect(() => {
        if (countryList.length === 0 && countries.length > 0) {
            setCountryList(countries);
        }
    }, [countries, countryList.length]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        paid_top_rate: true,
        paid_first_rate: true,
    });

    const handleChangeColumnVisibilityModel = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel({
            ...newModel,
            paid_top_rate: true,
            paid_first_rate: true,
        });
    }, []);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    const [modalPreview, setModalPreview] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalCreateConquestMode, setModalCreateConquestMode] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalDisabledConquestMode, setModalDisabledConquestMode] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const sourceRows = conquestModeData || [];
    const allMetricRows = sourceRows.flatMap((group: any) => [
        group,
        ...(group.children ?? []),
    ]);

    const maxClicks = Math.max(
        ...allMetricRows.map((row: any) =>
            getCurrentValue(row, 'ad_clicks') ?? 0
        ),
        0
    );

    const maxAdCost = Math.max(
        ...allMetricRows.map((row: any) =>
            getCurrentValue(row, 'ad_cost') ?? 0
        ),
        0
    );

    const maxConversions = Math.max(
        ...allMetricRows.map((row: any) =>
            getCurrentValue(row, 'ad_conversions') ?? 0
        ),
        0
    );

    const columns = [
        {
            field: 'competitor',
            headerName: 'Competitors',
            minWidth: 160,
            flex: 1.4,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                const competitor = params.row.competitor || '-';
                return (
                    <Box
                        title={competitor}
                        sx={{
                            width: '100%',
                            height: '100%',
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            px: '12px',
                            pl: params.row.isParent ? '12px' : '26px',
                            overflow: 'hidden',
                        }}
                    >
                        <CustomTooltip title={competitor} placement="top">
                            <Typography
                                sx={{
                                    width: '100%',
                                    minWidth: 0,
                                    color: '#333C47',
                                    fontFamily: '"Public Sans"',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    lineHeight: '22px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'help'
                                }}
                            >
                                {competitor}
                            </Typography>
                        </CustomTooltip>
                    </Box>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'left',
            minWidth: 170,
            maxWidth: 170,
            flex: 0.7,
            type: 'boolean',
            sortable: false,
            disableColumnMenu: false,
            valueGetter: (_value: any, row: any) => {
                if (row?.isParent) {
                    return null;
                }
                return row?.conquest_mode_data?.status === true;
            },
            cellClassName: 'mode-cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row;
                if (row.isParent) {
                    return null;
                }
                const isActive = row?.conquest_mode_data?.status === true;
                const handleAdd = (event: React.MouseEvent) => {
                    event.stopPropagation();
                    setModalCreateConquestMode({
                        open: true,
                        row: [row],
                    });
                };
                const handleEdit = (event: React.MouseEvent) => {
                    event.stopPropagation();
                    setModalPreview({
                        open: true,
                        row,
                    });
                };
                const handleDelete = (event: React.MouseEvent) => {
                    event.stopPropagation();
                    setModalDisabledConquestMode({
                        open: true,
                        row: [row],
                    });
                };
                if (!isActive) {
                    return (
                        <Box
                            onClick={handleAdd}
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '& .mode-add-button': {
                                    opacity: 0,
                                },
                                '&:hover .mode-add-button': {
                                    opacity: 1,
                                },
                            }}
                        >
                            <Box
                                className="mode-add-button"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: '-12px',
                                    width: '36px',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#F4F6F8',
                                    transition: 'opacity 0.15s ease',
                                }}
                            >
                                <Iconify
                                    icon="mingcute:add-line"
                                    width={18}
                                    height={18}
                                    sx={{ color: '#333C47' }}
                                />
                            </Box>
                        </Box>
                    );
                }
                return (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '28px',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                backgroundColor: '#EAF4FF',
                                maxWidth: '100%',
                                '& .mode-actions': {
                                    width: 0,
                                    opacity: 0,
                                    overflow: 'hidden',
                                    transition: 'width 0.15s ease, opacity 0.15s ease',
                                },
                                '&:hover .mode-actions': {
                                    width: '56px',
                                    opacity: 1,
                                },
                                '&:hover .mode-label': {
                                    maxWidth: '48px',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: '36px',
                                    height: '28px',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#D6E9FF',
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M11.7427 5.60177H16.7042C17.0976 5.60177 17.2944 5.60177 17.4094 5.68449C17.5098 5.75667 17.5752 5.86776 17.5895 5.99056C17.606 6.13131 17.5104 6.30328 17.3193 6.64722L16.1353 8.77854C16.066 8.90327 16.0313 8.96564 16.0177 9.03169C16.0057 9.09014 16.0057 9.15043 16.0177 9.20889C16.0313 9.27494 16.066 9.3373 16.1353 9.46204L17.3193 11.5934C17.5104 11.9373 17.606 12.1093 17.5895 12.25C17.5752 12.3728 17.5098 12.4839 17.4094 12.5561C17.2944 12.6388 17.0976 12.6388 16.7042 12.6388H10.5113C10.0186 12.6388 9.7723 12.6388 9.58414 12.5429C9.41862 12.4586 9.28406 12.324 9.19972 12.1585C9.10385 11.9704 9.10385 11.724 9.10385 11.2314V9.12029M6.02515 17.9166L2.50663 3.84251M3.82611 9.12029H10.3353C10.828 9.12029 11.0743 9.12029 11.2625 9.02441C11.428 8.94008 11.5625 8.80552 11.6469 8.64C11.7427 8.45184 11.7427 8.20552 11.7427 7.71288V3.49066C11.7427 2.99802 11.7427 2.7517 11.6469 2.56354C11.5625 2.39803 11.428 2.26346 11.2625 2.17913C11.0743 2.08325 10.828 2.08325 10.3353 2.08325H3.86937C3.25493 2.08325 2.94771 2.08325 2.73759 2.21057C2.55342 2.32215 2.41658 2.49741 2.35299 2.70315C2.28045 2.93788 2.35496 3.23593 2.50399 3.83202L3.82611 9.12029Z"
                                        stroke="#0052B4"
                                        strokeWidth="1.66667"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box
                                className="mode-label"
                                sx={{
                                    height: '28px',
                                    px: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: 0,
                                    flex: '0 1 auto',
                                    maxWidth: '90px',
                                    overflow: 'hidden',
                                    transition: 'max-width 0.15s ease',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#0052B4',
                                        fontFamily: '"Public Sans"',
                                        fontSize: '13px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        lineHeight: '22px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    Conquest
                                </Typography>
                            </Box>
                            <Box
                                className="mode-actions"
                                sx={{
                                    height: '28px',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#FFFFFF',
                                }}
                            >
                                <IconButton
                                    onClick={handleEdit}
                                    sx={{
                                        width: '28px',
                                        height: '28px',
                                        padding: 0,
                                        borderRadius: 0,
                                        color: '#0B6FE8',
                                        '&:hover': {
                                            backgroundColor: '#F4F6F8',
                                        },
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            d="M10.5001 1.16675L12.8334 3.50008M1.16675 12.8334L1.91132 10.1033C1.95989 9.92522 1.98418 9.83616 2.02147 9.75311C2.05457 9.67938 2.09525 9.60928 2.14285 9.54395C2.19646 9.47037 2.26173 9.4051 2.39228 9.27455L8.4201 3.24673C8.5356 3.13123 8.59336 3.07347 8.65995 3.05184C8.71853 3.0328 8.78163 3.0328 8.84021 3.05184C8.90681 3.07347 8.96456 3.13123 9.08006 3.24673L10.7534 4.9201C10.8689 5.0356 10.9267 5.09336 10.9483 5.15995C10.9674 5.21853 10.9674 5.28163 10.9483 5.34021C10.9267 5.40681 10.8689 5.46456 10.7534 5.58007L4.72561 11.6079C4.59506 11.7384 4.52979 11.8037 4.45621 11.8573C4.39088 11.9049 4.32079 11.9456 4.24705 11.9787C4.164 12.016 4.07494 12.0403 3.89683 12.0888L1.16675 12.8334Z"
                                            stroke="#006C9C"
                                            strokeOpacity="0.85"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </IconButton>
                                <IconButton
                                    onClick={handleDelete}
                                    sx={{
                                        width: '28px',
                                        height: '28px',
                                        padding: 0,
                                        borderRadius: 0,
                                        color: '#0B6FE8',
                                        '&:hover': {
                                            backgroundColor: '#F4F6F8',
                                        },
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            d="M5.25 1.75H8.75M1.75 3.5H12.25M11.0833 3.5L10.6742 9.63625C10.6129 10.5569 10.5822 11.0172 10.3833 11.3663C10.2083 11.6735 9.94422 11.9206 9.62597 12.0748C9.26448 12.25 8.80314 12.25 7.88045 12.25H6.11955C5.19686 12.25 4.73552 12.25 4.37403 12.0748C4.05577 11.9206 3.79172 11.6735 3.61666 11.3663C3.41781 11.0172 3.38713 10.5569 3.32575 9.63625L2.91667 3.5M5.83333 6.125V9.04167M8.16667 6.125V9.04167"
                                            stroke="#006C9C"
                                            strokeOpacity="0.85"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'paid_top_rate',
            cellClassName: (params: GridRenderCellParams) =>
                !params.row.isParent && params.row?.conquest_mode_data?.status === true
                    ? 'paid-rate-editable-cell'
                    : '',
            headerName: 'Paid Top %',
            type: 'number',
            align: 'right',
            headerAlign: 'left',
            minWidth: 100,
            flex: 0.85,
            sortable: true,
            disableColumnMenu: true,
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(row, 'paid_top_rate'),
            renderCell: (params: GridRenderCellParams) => {
                const current = getCurrentValue(
                    params.row,
                    'paid_top_rate'
                );
                const compare = getCompareValue(
                    params.row,
                    'paid_top_rate'
                );
                return (
                    <PaidRateCell
                        current={current}
                        compare={compare}
                        showEvolution={
                            showEvolution &&
                            selectedComparison !== 'none'
                        }
                        evolutionFormat={evolutionFormat}
                    />
                );
            },
        },
        {
            field: 'paid_first_rate',
            cellClassName: (params: GridRenderCellParams) =>
                !params.row.isParent && params.row?.conquest_mode_data?.status === true
                    ? 'paid-rate-editable-cell'
                    : '',
            headerName: 'Paid First %',
            type: 'number',
            align: 'right',
            headerAlign: 'left',
            minWidth: 100,
            flex: 0.85,
            sortable: true,
            disableColumnMenu: true,
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(row, 'paid_first_rate'),
            renderCell: (params: GridRenderCellParams) => {
                const current = getCurrentValue(
                    params.row,
                    'paid_first_rate'
                );
                const compare = getCompareValue(
                    params.row,
                    'paid_first_rate'
                );
                return (
                    <PaidRateCell
                        current={current}
                        compare={compare}
                        showEvolution={
                            showEvolution &&
                            selectedComparison !== 'none'
                        }
                        evolutionFormat={evolutionFormat}
                    />
                );
            },
        },
        {
            field: 'competitor_visibility_rate',
            headerName: 'Competitor visibility rate',
            headerClassName: 'visibility-rate-header',
            filterable: false,
            cellClassName: 'visibility-rate-cell',
            minWidth: 180,
            flex: 1.35,
            sortable: true,
            disableColumnMenu: true,
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(
                    row,
                    'competitor_visibility_rate',
                    'visibility_rate'
                ),
            renderCell: (params: GridRenderCellParams) => {
                const total = getCurrentValue(
                    params.row,
                    'competitor_visibility_rate',
                    'visibility_rate'
                );
                const compareTotal = getCompareValue(
                    params.row,
                    'competitor_visibility_rate',
                    'visibility_rate'
                );
                const data = {
                    organicOnly: getCurrentValue(
                        params.row,
                        'competitor_organic_rate',
                        'organic_rate'
                    ),
                    soloBidder: getCurrentValue(
                        params.row,
                        'competitor_solo_bidder',
                        'solo_bidder'
                    ),
                    topAdPosition: getCurrentValue(
                        params.row,
                        'competitor_top_ad',
                        'top_ad'
                    ),
                    competitorOnly: getCurrentValue(
                        params.row,
                        'competitor_only_rate',
                        'competitor_only'
                    ),
                };
                return (
                    <VisibilityRateCell
                        total={total}
                        compareTotal={compareTotal}
                        data={data}
                        colors={[
                            '#18A155',
                            '#E95954',
                            '#EF8783',
                            '#F7C2C0',
                        ]}
                        showEvolution={
                            showEvolution && selectedComparison !== 'none'
                        }
                        evolutionFormat={evolutionFormat}
                    />
                );
            },
        },
        {
            field: 'ad_clicks',
            align: 'left',
            headerAlign: 'left',
            headerName: 'Clicks',
            disableColumnMenu: true,
            minWidth: 125,
            flex: 1,
            type: 'number',
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(row, 'ad_clicks'),
            renderCell: (params: GridRenderCellParams) => {
                const current = getCurrentValue(params.row, 'ad_clicks');
                const compare = getCompareValue(params.row, 'ad_clicks');

                return (
                    <MetricProgressCell
                        value={current}
                        formattedValue={current === null ? "—" : formatNumber(current)}
                        maxValue={maxClicks}
                        color="#E6A11A"
                        valueWidth={40}
                        evolution={getAdsEvolution(current, compare)}
                        evolutionFormat={evolutionFormat === 'percent' ? 'percent' : 'absolute'}
                        showEvolution={
                            showEvolution &&
                            selectedComparison !== 'none'
                        }
                    />
                );
            },
        },
        {
            field: 'ad_cost',
            headerName: 'Ad Cost',
            disableColumnMenu: true,
            type: 'number',
            align: 'left',
            headerAlign: 'left',
            minWidth: 130,
            flex: 1,
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(row, 'ad_cost'),
            renderCell: (params: GridRenderCellParams) => {
                const current = getCurrentValue(params.row, 'ad_cost');
                const compare = getCompareValue(params.row, 'ad_cost');

                return (
                    <MetricProgressCell
                        value={current}
                        formattedValue={current === null ? "—" : formatCurrency(
                            current,
                            workspace.currency,
                            currentLang.value,
                            true,
                            1
                        )}
                        maxValue={maxAdCost}
                        color="#E6A11A"
                        valueWidth={48}
                        evolution={getAdsEvolution(current, compare)}
                        evolutionFormat={evolutionFormat === 'percent' ? 'percent' : 'absolute'}
                        formattedAbsoluteEvolution={current === null || compare === null ? undefined : formatCurrency(
                            Math.abs(current - compare), workspace.currency, currentLang.value, false, 2
                        )}
                        showEvolution={
                            showEvolution &&
                            selectedComparison !== 'none'
                        }
                    />
                );
            },
        },
        {
            field: 'avg_cpc',
            type: 'number',
            disableColumnMenu: true,
            headerName: 'CPC',
            align: 'right',
            headerAlign: 'left',
            minWidth: selectedComparison === 'none' ? 80 : 115,
            maxWidth: selectedComparison === 'none' ? 80 : 115,
            width: selectedComparison === 'none' ? 80 : 115,
            sortable: true,
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(row, 'avg_cpc'),
            renderCell: (params: GridRenderCellParams) => {
                const current = getCurrentValue(params.row, 'avg_cpc');
                const compare = getCompareValue(params.row, 'avg_cpc');
                const evolution = getAdsEvolution(current, compare);
                const hasComparison =
                    showEvolution &&
                    selectedComparison !== 'none';

                return (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: hasComparison
                                ? 'space-between'
                                : 'flex-end',
                        }}
                    >
                        <Typography
                            sx={{
                                flexShrink: 0,
                                color: '#2F3843',
                                textAlign: 'right',
                                fontFamily: '"Public Sans"',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '22px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {current === null ? "—" : formatCurrency(
                                current,
                                workspace.currency,
                                currentLang.value,
                                false,
                                2
                            )}
                        </Typography>

                        {hasComparison && (
                            <EvolutionValue
                                value={evolution}
                                format={evolutionFormat === 'percent' ? 'percent' : 'absolute'}
                                formattedAbsoluteValue={current === null || compare === null ? undefined : formatCurrency(
                                    Math.abs(current - compare), workspace.currency, currentLang.value, false, 2
                                )}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'ad_conversions',
            type: 'number',
            align: 'left',
            headerAlign: 'left',
            disableColumnMenu: true,
            headerName: 'Conversions',
            minWidth: 125,
            flex: 1,
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(row, 'ad_conversions'),
            renderCell: (params: GridRenderCellParams) => {
                const current = getCurrentValue(params.row, 'ad_conversions');
                const compare = getCompareValue(params.row, 'ad_conversions');
                return (
                    <MetricProgressCell
                        value={current}
                        formattedValue={current === null ? "—" : formatNumber(current)}
                        maxValue={maxConversions}
                        color="#0052B4"
                        valueWidth={36}
                        evolution={getAdsEvolution(current, compare)}
                        evolutionFormat={evolutionFormat === 'percent' ? 'percent' : 'absolute'}
                        showEvolution={
                            showEvolution && selectedComparison !== 'none'
                        }
                    />
                );
            },
        },
        {
            field: 'ad_revenue',
            type: 'number',
            align: 'right',
            headerAlign: 'left',
            disableColumnMenu: true,
            headerName: 'Revenue',
            minWidth: selectedComparison === 'none' ? 95 : 120,
            flex: 1,
            valueGetter: (_value: any, row: any) =>
                getCurrentValue(row, 'ad_revenue'),
            renderCell: (params: GridRenderCellParams) => {
                const current = getCurrentValue(params.row, 'ad_revenue');
                const compare = getCompareValue(params.row, 'ad_revenue');
                const evolution = getAdsEvolution(current, compare);
                const hasComparison =
                    showEvolution &&
                    selectedComparison !== 'none';
                return (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: hasComparison
                                ? 'space-between'
                                : 'flex-end',
                        }}
                    >
                        <Typography
                            sx={{
                                flexShrink: 0,
                                color: '#2F3843',
                                textAlign: 'right',
                                fontFamily: '"Public Sans"',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '22px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {current === null ? "—" : formatCurrency(
                                current,
                                workspace.currency,
                                currentLang.value,
                                true,
                                1
                            )}
                        </Typography>
                        {hasComparison && (
                            <EvolutionValue
                                value={evolution}
                                format={evolutionFormat === 'percent' ? 'percent' : 'absolute'}
                                formattedAbsoluteValue={current === null || compare === null ? undefined : formatCurrency(
                                    Math.abs(current - compare), workspace.currency, currentLang.value, false, 2
                                )}
                            />
                        )}
                    </Box>
                );
            },
        },
    ];

    const gridColumns = columns.map((column: any) => ({
        ...column,
        renderHeader:
            column.renderHeader ??
            (() => renderGridHeader(column.headerName ?? column.field)),
    }));

    const hasSelectedCountry = (filterStore?.countries?.length ?? 0) > 0;

    const isInitialLoading =
        isLoadingCountryData ||
        isLoadingCountries ||
        !hasSelectedCountry ||
        isLoadingConquestModeData ||
        isFetchingConquestModeData;

    const rawRows = sourceRows;

    const flattenedRows = rawRows.flatMap((adGroup: any) => {
        const parentRow = {
            ...adGroup,
            id: `parent-${adGroup.id}`,
            competitor: adGroup.ad_group_name,
            isParent: true
        };
        const childrenRows = (adGroup.children ?? []).map((kw: any) => ({
            ...kw,
            id: `child-${adGroup.id}-${kw.id}`,
            competitor: kw.keyword,
            isParent: false
        }));
        return [parentRow, ...childrenRows];
    });

    const rows = flattenedRows;
    const hasRows = rows.length > 0;

    const columnGroupingModel = [
        {
            groupId: 'ads_centric',
            headerName: 'Ads centric',
            headerClassName: "ads-centric-header",
            renderHeaderGroup: () => renderGridHeader('Ads centric', true),
            children: [
                { field: 'ad_clicks' },
                { field: 'ad_cost' },
                { field: 'avg_cpc' },
                { field: 'ad_conversions' },
                { field: 'ad_revenue' },
            ],
        },
        {
            groupId: 'scrapping_centric',
            headerName: 'Scrapping centric',
            headerClassName: "scrapping-centric-header",
            renderHeaderGroup: () => renderGridHeader('Scrapping centric', true),
            children: [
                { field: 'paid_top_rate' },
                { field: 'paid_first_rate' },
                { field: 'competitor_visibility_rate' },
            ],
        },
    ];

    const MAX_VISIBLE_ROWS = 10;
    const GRID_TOOLBAR_HEIGHT = 46;
    const GRID_GROUP_HEADER_HEIGHT = 22;
    const GRID_COLUMN_HEADER_HEIGHT = 36;
    const GRID_ROW_HEIGHT = 44;
    const GRID_FOOTER_HEIGHT = 40;
    const GRID_BORDER_HEIGHT = 2;
    const columnFields = new Set(gridColumns.map((column: any) => column.field));

    const hasVisibleColumnGroups = columnGroupingModel.some((group) =>
        group.children.some((child) =>
            columnFields.has(child.field)
        )
    );
    const currentPageStart = paginationModel.page * paginationModel.pageSize;

    const currentPageRowCount = Math.max(
        0,
        Math.min(
            paginationModel.pageSize,
            rows.length - currentPageStart
        )
    );

    const visibleRowCount = Math.min(
        currentPageRowCount,
        MAX_VISIBLE_ROWS
    );

    const gridHeight =
        GRID_TOOLBAR_HEIGHT +
        (hasVisibleColumnGroups ? GRID_GROUP_HEADER_HEIGHT : 0) +
        GRID_COLUMN_HEADER_HEIGHT +
        visibleRowCount * GRID_ROW_HEIGHT +
        GRID_FOOTER_HEIGHT +
        GRID_BORDER_HEIGHT;

    const handleCloseModalPreview = useCallback(() => setModalPreview({ open: false, row: null }), []);
    const handleCloseModalCreateConquestMode = useCallback(() => setModalCreateConquestMode({ open: false, row: null }), []);
    const handleCloseModalDisabledConquestMode = useCallback(() => setModalDisabledConquestMode({ open: false, row: null }), []);

    const handleCreateConquestMode = async (dataArray: any[]) => {
        const data = dataArray.map((data) => ({
            keyword: data?.keyword,
            keyword_id: String(data?.keyword_id),
            match_type: data?.match_type,
            account: data?.account_name,
            ad_group_name: data?.ad_group_name,
            platform: data?.platform,
            campaign_name: data?.campaign_name ? [data.campaign_name] : [],
            campaign_id: data?.campaign_id ? [String(data?.campaign_id)] : [],
            account_id: String(data?.account_id),
            ad_group_id: String(data?.ad_group_id),
            workspaceId: workspace.id,
            client: workspace?.bqTable,
            mode: `Conquest Mode - ${data?.strategy || "Paid"}`,
            max_cpc: data?.max_cpc ? parseFloat(data.max_cpc) : null,
            accepted_variation_threshold: data?.accepted_variation_threshold ? parseFloat(data.accepted_variation_threshold) : null,
            campaign_type: "Search",
            bidding_strategy_type: "Max CPC",
            domain: data?.domain || null,
            country: data?.country_names,
            country_code: data?.country_codes,
            home_language: data?.home_language,
        }));
        try {
            await addConquestMode({
                workspace_id: workspace.id,
                data,
            }).unwrap();
            setModalCreateConquestMode({ open: false, row: null });
            refetch();
            enqueueSnackbar(`Conquest Mode activated successfully`, { variant: "success" });
        } catch (err) {
            console.error("Error adding Conquest Mode:", err);
            enqueueSnackbar(`Error activating Conquest Mode`, { variant: "error" });
        }
    };

    const handleDisabledConquestMode = async (dataArray: any[]) => {
        const ids: number[] = dataArray
            .map((data) => data?.conquest_mode_data?.id)
            .filter((id): id is number => !!id);
        if (ids.length === 0) return;
        try {
            await disableConquestMode({
                ids,
                workspace_id: workspace.id,
            }).unwrap();
            refetch();
            enqueueSnackbar(`Conquest Mode disabled successfully`, { variant: "success" });
        } catch (err) {
            console.error("Error disabling Conquest Mode:", err);
            enqueueSnackbar(`Error disabling Conquest Mode`, { variant: "error" });
        }
    };

    const handleRestore = useCallback(
        (names: string[]) => {
            dispatch(
                setFilterCountry(names)
            );
        },
        [dispatch]
    );

    const { persist } = usePersistedCountries(countryList, handleRestore);

    const handleCountriesChange = (
        names: string[]
    ) => {
        dispatch(
            setFilterCountry(names)
        );
        persist(names);
    };

    return (
        <>
            <Box>
                <CountryBar
                    countries={countryList}
                    multiple
                    selectedCountries={
                        filterStore?.countries ??
                        []
                    }
                    onCountriesChange={
                        handleCountriesChange
                    }
                    loading={isLoadingCountryData}
                    stickyTop={
                        HEADER.H_DESKTOP * 2
                    }
                    backgroundColor="background.paper"
                    topGap={20}
                    contentPaddingX={
                        PAGE_PADDING_X
                    }
                    tabSpacing={12}
                    renderCountryLabel={(
                        country,
                        isSelected
                    ) => (
                        <CountryTabLabel
                            country={country}
                            isSelected={
                                isSelected
                            }
                        />
                    )}
                />

                <Box
                    px={'24px'}
                    mt={isInitialLoading || countryList.length > 1 ? '10px' : '0px'}
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    {isInitialLoading
                        ? <Skeleton width={'100%'} height={680} variant="rectangular" animation="wave" sx={{
                            borderRadius: '8px',
                        }} />
                        :
                        <DataGridPro
                            className={`conquest-mode-grid${currentPageRowCount > MAX_VISIBLE_ROWS
                                ? ' conquest-mode-grid--scrollable'
                                : ''
                                }`}
                            style={{ height: `${gridHeight}px` }}
                            sx={{
                                width: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box',
                                border: '1px solid #EEEEEE',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0px 2px 8px rgba(145, 158, 171, 0.08)',
                                '& .MuiDataGrid-main': {
                                    minWidth: 0,
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                    overflowX: 'auto',
                                },
                                '& .MuiDataGrid-row': {
                                    borderBottom: '1px solid rgba(145, 158, 171, 0.2)',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: 'none',
                                    borderRight: '1px solid rgba(145, 158, 171, 0.2)',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    borderRight: '1px solid rgba(145, 158, 171, 0.2)',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '1px solid rgba(145, 158, 171, 0.2)',
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: '1px solid rgba(145, 158, 171, 0.2)',
                                    minHeight: `${GRID_FOOTER_HEIGHT}px`,
                                },
                                '& .paid-rate-editable-cell': {
                                    cursor: 'pointer',
                                },
                            }}
                            apiRef={apiRef}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
                            columns={gridColumns}
                            onCellClick={(params) => {
                                if (params.field !== 'paid_top_rate' && params.field !== 'paid_first_rate') return;
                                if (params.row.isParent || params.row?.conquest_mode_data?.status !== true) return;

                                setModalPreview({
                                    open: true,
                                    row: params.row,
                                });
                            }}
                            autoHeight={false}
                            pagination={hasRows}
                            columnGroupingModel={columnGroupingModel}
                            columnGroupHeaderHeight={GRID_GROUP_HEADER_HEIGHT}
                            columnHeaderHeight={GRID_COLUMN_HEADER_HEIGHT}
                            rowHeight={GRID_ROW_HEIGHT}
                            hideFooter={!hasRows}
                            rows={rows}
                            hideFooterPagination={!hasRows}
                            hideFooterSelectedRowCount
                            density="standard"
                            disableRowSelectionOnClick
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            slots={{
                                panel: (props) => (
                                    <CustomGridPanel
                                        {...props}
                                        toolbarRef={toolbarRef}
                                    />
                                ),
                                toolbar: () => (
                                    <CustomToolbar
                                        apiRef={apiRef}
                                        toolbarRef={toolbarRef}
                                        selectedPeriod={selectedPeriod}
                                        onPeriodChange={setSelectedPeriod}
                                        selectedComparison={selectedComparison}
                                        onComparisonChange={setSelectedComparison}
                                        onEvolutionChange={setShowEvolution}
                                        evolutionFormat={evolutionFormat}
                                        onEvolutionFormatChange={setEvolutionFormat}
                                        onOpenAnalysisChart={() => setShowAnalysisChart(true)}
                                    />
                                ),
                                footer: hasRows
                                    ? () => (
                                        <ConquestModeFooter
                                            paginationModel={paginationModel}
                                            onPageSizeChange={(pageSize: number) =>
                                                setPaginationModel((prev) => ({
                                                    ...prev,
                                                    pageSize,
                                                    page: 0,
                                                }))
                                            }
                                            onPageChange={(page: number) =>
                                                setPaginationModel((prev) => ({
                                                    ...prev,
                                                    page,
                                                }))
                                            }
                                            rows={rows}
                                        />
                                    )
                                    : undefined,
                            }}
                        />
                    }
                </Box>

                <ModalPreview
                    open={modalPreview.open}
                    row={modalPreview.row}
                    onClose={handleCloseModalPreview}
                />

                <ModalCreateConquestMode
                    open={modalCreateConquestMode.open}
                    row={modalCreateConquestMode.row}
                    onClose={handleCloseModalCreateConquestMode}
                    onSubmit={handleCreateConquestMode}
                />

                <ModalDisabledConquestMode
                    open={modalDisabledConquestMode.open}
                    row={modalDisabledConquestMode.row}
                    onClose={handleCloseModalDisabledConquestMode}
                    onSubmit={handleDisabledConquestMode}
                />

                <ConquestModeAnalysisChartModal
                    open={showAnalysisChart}
                    onClose={() => setShowAnalysisChart(false)}
                />
            </Box >
        </>
    )
}